import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Copy, Trash2, ExternalLink, Download, ChevronDown, ChevronRight, Globe, FileText, Link2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGION_LABELS = {
  NA: "NA",
  EME: "EME",
  APA: "APA",
  Global: "Global",
};

export default function ArchivePage() {
  usePageTitle("Link Repository");
  const [groupedLinks, setGroupedLinks] = useState([]);
  const [dealerGroupedLinks, setDealerGroupedLinks] = useState([]);
  const [itgGroupedLinks, setItgGroupedLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLinks, setSelectedLinks] = useState(new Set());
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("standard");

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const [standardResponse, dealerResponse, itgResponse] = await Promise.all([
        axios.get(`${API}/links/grouped`),
        axios.get(`${API}/dealer-links/grouped`),
        axios.get(`${API}/itg-links/grouped`)
      ]);
      setGroupedLinks(standardResponse.data);
      setDealerGroupedLinks(dealerResponse.data);
      setItgGroupedLinks(itgResponse.data);
      // Expand all regions by default
      const regionKeys = new Set([
        ...standardResponse.data.map(r => r.region),
        ...dealerResponse.data.map(r => `dealer-${r.region}`),
        ...itgResponse.data.map(r => `itg-${r.region}`)
      ]);
      setExpandedRegions(regionKeys);
    } catch (error) {
      console.error("Error fetching links:", error);
      toast.error("Failed to load links");
    } finally {
      setLoading(false);
    }
  };

  const toggleRegion = (region) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(region)) {
      newExpanded.delete(region);
    } else {
      newExpanded.add(region);
    }
    setExpandedRegions(newExpanded);
  };

  const toggleCampaign = (key) => {
    const newExpanded = new Set(expandedCampaigns);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedCampaigns(newExpanded);
  };

  const toggleLinkSelection = (linkId) => {
    const newSelected = new Set(selectedLinks);
    if (newSelected.has(linkId)) {
      newSelected.delete(linkId);
    } else {
      newSelected.add(linkId);
    }
    setSelectedLinks(newSelected);
  };

  const selectAllLinks = () => {
    const allLinkIds = new Set();
    const linksToSelect = activeTab === "dealer" ? dealerGroupedLinks : activeTab === "itg" ? itgGroupedLinks : groupedLinks;
    linksToSelect.forEach(region => {
      region.campaigns.forEach(campaign => {
        campaign.links.forEach(link => {
          if (matchesSearch(link)) {
            allLinkIds.add(link.id);
          }
        });
      });
    });
    setSelectedLinks(allLinkIds);
  };

  const deselectAllLinks = () => {
    setSelectedLinks(new Set());
  };

  // Extract dealer name from redirect base URL
  const getDealerName = (redirectBaseUrl) => {
    if (!redirectBaseUrl) return "";
    // Extract the last segment of the path (e.g., "agwood" from ".../redirect/agwood")
    const parts = redirectBaseUrl.replace(/\/$/, "").split("/");
    return parts[parts.length - 1] || "";
  };

  const matchesSearch = (link) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    // Base search fields
    const baseMatch = (
      link.campaign.toLowerCase().includes(query) ||
      link.source.toLowerCase().includes(query) ||
      link.medium.toLowerCase().includes(query) ||
      link.brand.toLowerCase().includes(query) ||
      link.generated_url.toLowerCase().includes(query) ||
      (link.content && link.content.toLowerCase().includes(query)) ||
      (link.term && link.term.toLowerCase().includes(query))
    );
    
    // Dealer-specific search fields
    if (link.redirect_base_url) {
      const dealerName = getDealerName(link.redirect_base_url);
      return baseMatch || 
        dealerName.toLowerCase().includes(query) ||
        (link.destination_path && link.destination_path.toLowerCase().includes(query));
    }
    
    return baseMatch;
  };

  const getTotalLinksCount = () => {
    let count = 0;
    const linksToCount = activeTab === "dealer" ? dealerGroupedLinks : activeTab === "itg" ? itgGroupedLinks : groupedLinks;
    linksToCount.forEach(region => {
      region.campaigns.forEach(campaign => {
        campaign.links.forEach(link => {
          if (matchesSearch(link)) count++;
        });
      });
    });
    return count;
  };

  const getDealerLinksCount = () => {
    let count = 0;
    dealerGroupedLinks.forEach(region => {
      region.campaigns.forEach(campaign => {
        count += campaign.links.length;
      });
    });
    return count;
  };

  const getStandardLinksCount = () => {
    let count = 0;
    groupedLinks.forEach(region => {
      region.campaigns.forEach(campaign => {
        count += campaign.links.length;
      });
    });
    return count;
  };

  const getItgLinksCount = () => {
    let count = 0;
    itgGroupedLinks.forEach(region => {
      region.campaigns.forEach(campaign => {
        count += campaign.links.length;
      });
    });
    return count;
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const deleteLink = async (id) => {
    try {
      const endpoint = activeTab === "dealer" ? `${API}/dealer-links/${id}` : activeTab === "itg" ? `${API}/itg-links/${id}` : `${API}/links/${id}`;
      await axios.delete(endpoint);
      toast.success("Link deleted");
      fetchLinks();
      selectedLinks.delete(id);
      setSelectedLinks(new Set(selectedLinks));
    } catch (error) {
      toast.error("Failed to delete link");
    }
  };

  const deleteSelectedLinks = async () => {
    if (selectedLinks.size === 0) {
      toast.error("Please select links to delete");
      return;
    }

    setDeleting(true);
    try {
      const endpoint = activeTab === "dealer" ? `${API}/dealer-links` : activeTab === "itg" ? `${API}/itg-links` : `${API}/links`;
      const deletePromises = Array.from(selectedLinks).map(id => 
        axios.delete(`${endpoint}/${id}`)
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedLinks.size} links deleted`);
      setSelectedLinks(new Set());
      fetchLinks();
    } catch (error) {
      toast.error("Failed to delete some links");
      fetchLinks();
    } finally {
      setDeleting(false);
    }
  };

  const exportLinks = async (exportAll = false) => {
    const linkIds = exportAll ? ["all"] : Array.from(selectedLinks);
    
    if (linkIds.length === 0) {
      toast.error("Please select links to export");
      return;
    }

    setExporting(true);
    try {
      const linkType = activeTab === "dealer" ? "dealer_redirect" : activeTab === "itg" ? "itg" : "standard";
      const response = await axios.post(
        `${API}/links/export`,
        { link_ids: linkIds, link_type: linkType },
        { responseType: "blob" }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const prefix = activeTab === "dealer" ? "dealer_redirect_links" : activeTab === "itg" ? "itg_links" : "utm_links";
      link.setAttribute("download", `${prefix}_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export downloaded!");
    } catch (error) {
      toast.error("Failed to export links");
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const truncateUrl = (url, maxLength = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  return (
    <div className="p-8" data-testid="archive-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-zinc-950 red-thread pb-3">
          Link Repository
        </h1>
        <p className="text-sm text-zinc-500 mt-4">
          View and manage all your generated UTM links by region and campaign
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6 items-center justify-between">
        {/* Search */}
        <div className="relative w-full max-w-md">
          <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={activeTab === "dealer" || activeTab === "itg"
              ? "Search by campaign, destination, source, medium..." 
              : "Search by campaign, source, medium, URL, content, term..."}
            className="input-with-icon"
            data-testid="search-input"
          />
        </div>

        {/* Export Controls */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-zinc-500">
            {selectedLinks.size} of {getTotalLinksCount()} selected
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={selectAllLinks}
            className="rounded-none text-xs"
            data-testid="select-all-btn"
          >
            Select All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={deselectAllLinks}
            className="rounded-none text-xs"
            data-testid="deselect-all-btn"
          >
            Clear
          </Button>
          <Button
            onClick={deleteSelectedLinks}
            disabled={selectedLinks.size === 0 || deleting}
            variant="outline"
            className="rounded-none h-9 text-xs px-4 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            data-testid="delete-selected-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {deleting ? "Deleting..." : "Delete Selected"}
          </Button>
          <Button
            onClick={() => exportLinks(false)}
            disabled={selectedLinks.size === 0 || exporting}
            className="btn-secondary h-9 text-xs px-4"
            data-testid="export-selected-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Export Selected
          </Button>
          <Button
            onClick={() => exportLinks(true)}
            disabled={exporting}
            className="btn-primary h-9 text-xs px-4"
            data-testid="export-all-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Export All
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 flex items-center gap-4">
        <div className="text-sm text-zinc-500">
          Total: <span className="font-bold text-zinc-950">{getTotalLinksCount()}</span> links in this tab
        </div>
      </div>

      {/* Tabs for Standard vs Dealer Links */}
      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setSelectedLinks(new Set()); }} className="space-y-4">
        <TabsList className="bg-zinc-100 p-1 rounded-none">
          <TabsTrigger
            value="standard"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
            data-testid="standard-links-tab"
          >
            <FileText className="w-4 h-4 mr-2" />
            Standard Links ({getStandardLinksCount()})
          </TabsTrigger>
          <TabsTrigger
            value="dealer"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
            data-testid="dealer-links-tab"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Dealer Redirect ({getDealerLinksCount()})
          </TabsTrigger>
          <TabsTrigger
            value="itg"
            className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
            data-testid="itg-links-tab"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Dealer Redirect ITG ({getItgLinksCount()})
          </TabsTrigger>
        </TabsList>

        {/* Standard Links Tab */}
        <TabsContent value="standard">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Loading...</div>
          ) : groupedLinks.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 border border-zinc-200 bg-white">
              No standard links created yet. Start building!
            </div>
          ) : (
            <div className="space-y-4" data-testid="grouped-links">
              {groupedLinks.map((regionData) => {
                const filteredCampaigns = regionData.campaigns
                  .map(campaign => ({
                    ...campaign,
                    links: campaign.links.filter(matchesSearch)
                  }))
                  .filter(campaign => campaign.links.length > 0);

                if (filteredCampaigns.length === 0) return null;

                const regionLinkCount = filteredCampaigns.reduce((sum, c) => sum + c.links.length, 0);

                return (
                  <Collapsible
                    key={regionData.region}
                    open={expandedRegions.has(regionData.region)}
                    onOpenChange={() => toggleRegion(regionData.region)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-4 bg-[#AE132A] text-white hover:bg-[#8A0F20] transition-colors">
                        <div className="flex items-center gap-3">
                          {expandedRegions.has(regionData.region) ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                          <Globe className="w-5 h-5" />
                          <span className="font-bold uppercase tracking-wider">
                            {regionData.region} - {REGION_LABELS[regionData.region] || regionData.region}
                          </span>
                        </div>
                        <span className="text-sm opacity-80">
                          {regionLinkCount} links
                        </span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border border-t-0 border-zinc-200">
                        {filteredCampaigns.map((campaign) => {
                          const campaignKey = `${regionData.region}-${campaign.campaign}`;
                          return (
                            <Collapsible
                              key={campaignKey}
                              open={expandedCampaigns.has(campaignKey)}
                              onOpenChange={() => toggleCampaign(campaignKey)}
                            >
                              <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between p-3 bg-zinc-100 hover:bg-zinc-200 transition-colors border-b border-zinc-200">
                                  <div className="flex items-center gap-2">
                                    {expandedCampaigns.has(campaignKey) ? (
                                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                                    )}
                                    <span className="font-mono text-sm font-medium">
                                      {campaign.campaign}
                                    </span>
                                  </div>
                                  <span className="text-xs text-zinc-500">
                                    {campaign.links.length} links
                                  </span>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="table-header hover:bg-zinc-100">
                                      <TableHead className="w-12">
                                        <Checkbox
                                          checked={campaign.links.every(l => selectedLinks.has(l.id))}
                                          onCheckedChange={(checked) => {
                                            const newSelected = new Set(selectedLinks);
                                            campaign.links.forEach(l => {
                                              if (checked) {
                                                newSelected.add(l.id);
                                              } else {
                                                newSelected.delete(l.id);
                                              }
                                            });
                                            setSelectedLinks(newSelected);
                                          }}
                                        />
                                      </TableHead>
                                      <TableHead className="font-semibold">Date</TableHead>
                                      <TableHead className="font-semibold">Source</TableHead>
                                      <TableHead className="font-semibold">Medium</TableHead>
                                      <TableHead className="font-semibold">Brand</TableHead>
                                      <TableHead className="font-semibold">Channel</TableHead>
                                      <TableHead className="font-semibold">URL</TableHead>
                                      <TableHead className="font-semibold text-right">Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {campaign.links.map((link) => (
                                      <TableRow key={link.id} className="hover:bg-zinc-50" data-testid={`link-row-${link.id}`}>
                                        <TableCell>
                                          <Checkbox
                                            checked={selectedLinks.has(link.id)}
                                            onCheckedChange={() => toggleLinkSelection(link.id)}
                                          />
                                        </TableCell>
                                        <TableCell className="text-sm text-zinc-600">
                                          {formatDate(link.created_at)}
                                        </TableCell>
                                        <TableCell className="text-sm">{link.source}</TableCell>
                                        <TableCell className="text-sm">{link.medium}</TableCell>
                                        <TableCell className="text-sm">{link.brand}</TableCell>
                                        <TableCell>
                                          <span className="inline-block px-2 py-1 text-xs font-medium bg-zinc-100 text-zinc-700">
                                            {link.channel}
                                          </span>
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-zinc-500 max-w-xs">
                                          <div className="flex items-center gap-2">
                                            <span title={link.generated_url}>
                                              {truncateUrl(link.generated_url)}
                                            </span>
                                            <a
                                              href={link.generated_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-zinc-400 hover:text-[#AE132A]"
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                            </a>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <div className="flex items-center justify-end gap-2">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => copyUrl(link.generated_url)}
                                              className="h-8 w-8 p-0 hover:text-[#AE132A]"
                                              data-testid={`copy-link-${link.id}`}
                                            >
                                              <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => deleteLink(link.id)}
                                              className="h-8 w-8 p-0 hover:text-red-600"
                                              data-testid={`delete-link-${link.id}`}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </CollapsibleContent>
                            </Collapsible>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Dealer Redirect Links Tab */}
        <TabsContent value="dealer">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Loading...</div>
          ) : dealerGroupedLinks.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 border border-zinc-200 bg-white">
              No dealer redirect links created yet. Use the &quot;Dealer Redirect&quot; tab in the Builder.
            </div>
          ) : (
            <div className="space-y-4" data-testid="dealer-grouped-links">
              {dealerGroupedLinks.map((regionData) => {
                const filteredCampaigns = regionData.campaigns
                  .map(campaign => ({
                    ...campaign,
                    links: campaign.links.filter(matchesSearch)
                  }))
                  .filter(campaign => campaign.links.length > 0);

                if (filteredCampaigns.length === 0) return null;

                const regionLinkCount = filteredCampaigns.reduce((sum, c) => sum + c.links.length, 0);
                const dealerRegionKey = `dealer-${regionData.region}`;

                return (
                  <Collapsible
                    key={dealerRegionKey}
                    open={expandedRegions.has(dealerRegionKey)}
                    onOpenChange={() => toggleRegion(dealerRegionKey)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-4 bg-[#AE132A] text-white hover:bg-[#8A0F20] transition-colors">
                        <div className="flex items-center gap-3">
                          {expandedRegions.has(dealerRegionKey) ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                          <Globe className="w-5 h-5" />
                          <span className="font-bold uppercase tracking-wider">
                            {regionData.region} - {REGION_LABELS[regionData.region] || regionData.region}
                          </span>
                        </div>
                        <span className="text-sm opacity-80">
                          {regionLinkCount} links
                        </span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border border-t-0 border-zinc-200">
                        {filteredCampaigns.map((campaign) => {
                          const campaignKey = `dealer-${regionData.region}-${campaign.campaign}`;
                          return (
                            <Collapsible
                              key={campaignKey}
                              open={expandedCampaigns.has(campaignKey)}
                              onOpenChange={() => toggleCampaign(campaignKey)}
                            >
                              <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between p-3 bg-zinc-100 hover:bg-zinc-200 transition-colors border-b border-zinc-200">
                                  <div className="flex items-center gap-2">
                                    {expandedCampaigns.has(campaignKey) ? (
                                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                                    )}
                                    <span className="font-mono text-sm font-medium">
                                      {campaign.campaign}
                                    </span>
                                  </div>
                                  <span className="text-xs text-zinc-500">
                                    {campaign.links.length} links
                                  </span>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="table-header hover:bg-zinc-100">
                                      <TableHead className="w-12">
                                        <Checkbox
                                          checked={campaign.links.every(l => selectedLinks.has(l.id))}
                                          onCheckedChange={(checked) => {
                                            const newSelected = new Set(selectedLinks);
                                            campaign.links.forEach(l => {
                                              if (checked) {
                                                newSelected.add(l.id);
                                              } else {
                                                newSelected.delete(l.id);
                                              }
                                            });
                                            setSelectedLinks(newSelected);
                                          }}
                                        />
                                      </TableHead>
                                      <TableHead className="font-semibold">Date</TableHead>
                                      <TableHead className="font-semibold">Dealer Name</TableHead>
                                      <TableHead className="font-semibold">Destination Path</TableHead>
                                      <TableHead className="font-semibold">Source</TableHead>
                                      <TableHead className="font-semibold">Medium</TableHead>
                                      <TableHead className="font-semibold">Channel</TableHead>
                                      <TableHead className="font-semibold">Generated URL</TableHead>
                                      <TableHead className="font-semibold text-right">Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {campaign.links.map((link) => (
                                      <TableRow key={link.id} className="hover:bg-zinc-50" data-testid={`dealer-link-row-${link.id}`}>
                                        <TableCell>
                                          <Checkbox
                                            checked={selectedLinks.has(link.id)}
                                            onCheckedChange={() => toggleLinkSelection(link.id)}
                                          />
                                        </TableCell>
                                        <TableCell className="text-sm text-zinc-600">
                                          {formatDate(link.created_at)}
                                        </TableCell>
                                        <TableCell className="text-sm font-medium text-[#AE132A]">
                                          {getDealerName(link.redirect_base_url)}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-zinc-700">
                                          {link.destination_path}
                                        </TableCell>
                                        <TableCell className="text-sm">{link.source}</TableCell>
                                        <TableCell className="text-sm">{link.medium}</TableCell>
                                        <TableCell>
                                          <span className="inline-block px-2 py-1 text-xs font-medium bg-zinc-100 text-zinc-700">
                                            {link.channel}
                                          </span>
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-zinc-500 max-w-xs">
                                          <div className="flex items-center gap-2">
                                            <span title={link.generated_url}>
                                              {truncateUrl(link.generated_url, 40)}
                                            </span>
                                            <a
                                              href={link.generated_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-zinc-400 hover:text-[#AE132A]"
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                            </a>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <div className="flex items-center justify-end gap-2">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => copyUrl(link.generated_url)}
                                              className="h-8 w-8 p-0 hover:text-[#AE132A]"
                                              data-testid={`copy-dealer-link-${link.id}`}
                                            >
                                              <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => deleteLink(link.id)}
                                              className="h-8 w-8 p-0 hover:text-red-600"
                                              data-testid={`delete-dealer-link-${link.id}`}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </CollapsibleContent>
                            </Collapsible>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ITG Links Tab */}
        <TabsContent value="itg">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Loading...</div>
          ) : itgGroupedLinks.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 border border-zinc-200 bg-white">
              No ITG links created yet. Use the &quot;Dealer Redirect ITG&quot; tab in the Builder.
            </div>
          ) : (
            <div className="space-y-4" data-testid="itg-grouped-links">
              {itgGroupedLinks.map((regionData) => {
                const filteredCampaigns = regionData.campaigns
                  .map(campaign => ({
                    ...campaign,
                    links: campaign.links.filter(matchesSearch)
                  }))
                  .filter(campaign => campaign.links.length > 0);

                if (filteredCampaigns.length === 0) return null;

                const regionLinkCount = filteredCampaigns.reduce((sum, c) => sum + c.links.length, 0);
                const itgRegionKey = `itg-${regionData.region}`;

                return (
                  <Collapsible
                    key={itgRegionKey}
                    open={expandedRegions.has(itgRegionKey)}
                    onOpenChange={() => toggleRegion(itgRegionKey)}
                  >
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-4 bg-[#AE132A] text-white hover:bg-[#8A0F20] transition-colors">
                        <div className="flex items-center gap-3">
                          {expandedRegions.has(itgRegionKey) ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                          <Globe className="w-5 h-5" />
                          <span className="font-bold uppercase tracking-wider">
                            {regionData.region} - {REGION_LABELS[regionData.region] || regionData.region}
                          </span>
                        </div>
                        <span className="text-sm opacity-80">
                          {regionLinkCount} links
                        </span>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border border-t-0 border-zinc-200">
                        {filteredCampaigns.map((campaign) => {
                          const campaignKey = `itg-${regionData.region}-${campaign.campaign}`;
                          return (
                            <Collapsible
                              key={campaignKey}
                              open={expandedCampaigns.has(campaignKey)}
                              onOpenChange={() => toggleCampaign(campaignKey)}
                            >
                              <CollapsibleTrigger className="w-full">
                                <div className="flex items-center justify-between p-3 bg-zinc-100 hover:bg-zinc-200 transition-colors border-b border-zinc-200">
                                  <div className="flex items-center gap-2">
                                    {expandedCampaigns.has(campaignKey) ? (
                                      <ChevronDown className="w-4 h-4 text-zinc-500" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                                    )}
                                    <span className="font-mono text-sm font-medium">
                                      {campaign.campaign}
                                    </span>
                                  </div>
                                  <span className="text-xs text-zinc-500">
                                    {campaign.links.length} links
                                  </span>
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <Table>
                                  <TableHeader>
                                    <TableRow className="table-header hover:bg-zinc-100">
                                      <TableHead className="w-12">
                                        <Checkbox
                                          checked={campaign.links.every(l => selectedLinks.has(l.id))}
                                          onCheckedChange={(checked) => {
                                            const newSelected = new Set(selectedLinks);
                                            campaign.links.forEach(l => {
                                              if (checked) {
                                                newSelected.add(l.id);
                                              } else {
                                                newSelected.delete(l.id);
                                              }
                                            });
                                            setSelectedLinks(newSelected);
                                          }}
                                        />
                                      </TableHead>
                                      <TableHead className="font-semibold">Date</TableHead>
                                      <TableHead className="font-semibold">Destination Path</TableHead>
                                      <TableHead className="font-semibold">Source</TableHead>
                                      <TableHead className="font-semibold">Medium</TableHead>
                                      <TableHead className="font-semibold">Channel</TableHead>
                                      <TableHead className="font-semibold">Generated URL</TableHead>
                                      <TableHead className="font-semibold text-right">Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {campaign.links.map((link) => (
                                      <TableRow key={link.id} className="hover:bg-zinc-50" data-testid={`itg-link-row-${link.id}`}>
                                        <TableCell>
                                          <Checkbox
                                            checked={selectedLinks.has(link.id)}
                                            onCheckedChange={() => toggleLinkSelection(link.id)}
                                          />
                                        </TableCell>
                                        <TableCell className="text-sm text-zinc-600">
                                          {formatDate(link.created_at)}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-zinc-700">
                                          {link.destination_path}
                                        </TableCell>
                                        <TableCell className="text-sm">{link.source}</TableCell>
                                        <TableCell className="text-sm">{link.medium}</TableCell>
                                        <TableCell>
                                          <span className="inline-block px-2 py-1 text-xs font-medium bg-zinc-100 text-zinc-700">
                                            {link.channel}
                                          </span>
                                        </TableCell>
                                        <TableCell className="font-mono text-xs text-zinc-500 max-w-xs">
                                          <div className="flex items-center gap-2">
                                            <span title={link.generated_url}>
                                              {truncateUrl(link.generated_url, 40)}
                                            </span>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <div className="flex items-center justify-end gap-2">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => copyUrl(link.generated_url)}
                                              className="h-8 w-8 p-0 hover:text-[#AE132A]"
                                              data-testid={`copy-itg-link-${link.id}`}
                                            >
                                              <Copy className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => deleteLink(link.id)}
                                              className="h-8 w-8 p-0 hover:text-red-600"
                                              data-testid={`delete-itg-link-${link.id}`}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </CollapsibleContent>
                            </Collapsible>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
